import {useNavigate} from "react-router"
import {toast} from "react-toastify"
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import {isAxiosError} from "axios"

import api, {queryKey} from "../services"
import requestError from "../services/requestError.tsx"
import {ASettings, Users} from "../services/types.generated.ts"
import {getLSItem, setLSItem} from "../utils/localStorage"
import {tabId} from "../utils/tabId.ts"

export function useUserSettingsQuery() {
  return useQuery({
    queryKey: queryKey.userSettings,
    queryFn: fetchUserSettings,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    throwOnError: false,
    retry: (i, error) => {
      if (isAxiosError(error) && error.response?.status === 401) {
        return false
      }
      return i < 3
    },
  })
}

export function useCountriesQuery() {
  return useQuery({
    queryKey: queryKey.countries,
    queryFn: async () => (await api.countries.countriesList({per_page: 999})).data.countries,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    throwOnError: false,
  })
}

export function useUserSettingsOrLogout() {
  const queryClient = useQueryClient()

  const settings = queryClient.getQueryData<ASettings>(queryKey.userSettings)

  if (!settings) {
    window.location.replace("/log-in")
    throw new Error("User is not logged in")
  }
  return settings
}

export async function fetchUserSettings() {
  const settings = (await api.settings.settingsList()).data.settings

  if (settings && settings.user.id !== getLSItem("currentUserId")?.data.userId) {
    setLSItem("currentUserId", {userId: settings.user.id, tabId})
  }

  return settings
}

export type TLogInValues = NonNullable<Users.SignInCreate.RequestBody["user"]>

export function useLoginMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (user: TLogInValues) => {
      await api.users.signInCreate({user})
      await queryClient.invalidateQueries()
      await queryClient.prefetchQuery({queryKey: queryKey.userSettings, queryFn: fetchUserSettings})
    },
  })
}

export type TPasswordResetRequestValues = NonNullable<Users.PasswordCreate.RequestBody["user"]>

export function usePasswordResetRequestMutation() {
  return useMutation({
    mutationFn: (user: TPasswordResetRequestValues) => api.users.passwordCreate({user}),
  })
}

export type TNewPasswordValues = NonNullable<Users.PasswordUpdate.RequestBody["user"]>

export function useSetNewPasswordMutation() {
  return useMutation({
    mutationFn: (user: TNewPasswordValues) => api.users.passwordUpdate({user}),
  })
}

export function useLogoutMutation() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: () => api.users.signOutDelete(),
    mutationKey: queryKey.logout,
    onSuccess: async () => {
      await queryClient.resetQueries()

      if (getLSItem("currentUserId")?.data?.userId !== null) {
        setLSItem("currentUserId", {userId: null, tabId})
      }

      navigate("/log-in")
    },
    onError: e => {
      requestError(e)
    },
  })
}

export function useMasqueradeTargetsQuery() {
  return useQuery({
    queryKey: queryKey.masqueradeTargets,
    queryFn: async () => {
      const response = await api.masqueradeTargets.masqueradeTargetsList()
      return response.data
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 3,
    throwOnError: false,
  })
}

export function useStartMasqueradeMutation() {
  return useMutation({
    mutationFn: async (userId: number) => {
      const response = await api.masqueradeSessions.masqueradeSessionsCreate({
        user_id: userId,
      })
      return response.data
    },
    onError: (error: unknown) => {
      // Don't show error toast if request was interrupted by navigation (successful redirect)
      // ERR_NETWORK with no response indicates the page navigated away during the request
      if (isAxiosError(error) && (error.code === "ERR_NETWORK" || error.code === "ERR_CANCELED") && !error.response) {
        return
      }
      const errorMessage =
        isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : "Failed to start masquerade session"
      toast.error(errorMessage)
    },
  })
}

export function useEndMasqueradeMutation() {
  return useMutation({
    mutationFn: async () => {
      const response = await api.masqueradeSessions.masqueradeSessionsDelete()
      return response.data
    },
    onError: (error: unknown) => {
      // Don't show error toast if request was interrupted by navigation (successful redirect)
      // ERR_NETWORK with no response indicates the page navigated away during the request
      if (isAxiosError(error) && (error.code === "ERR_NETWORK" || error.code === "ERR_CANCELED") && !error.response) {
        return
      }
      const errorMessage =
        isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : "Failed to end masquerade session"
      toast.error(errorMessage)
    },
  })
}
